// src/components/AnalyticsPanel.js
import React, { useMemo, useState, useEffect } from 'react';

// Recharts
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, RadialBarChart, RadialBar, Treemap
} from 'recharts';

// Simple chart palette (accessible, higher contrast)
const CHART_COLORS = {
  axis: '#64748b',      // slate-500
  grid: '#e2e8f0',      // slate-200
  line: '#4f46e5',      // indigo-600
  lineDot: '#3730a3',   // indigo-800
  line2: '#f59e0b',     // amber-500
  bar: '#7c3aed',       // violet-600
  barAlt: '#a78bfa',    // violet-300
  tooltipBg: '#0b1220', // deep navy
  tooltipFg: '#f8fafc', // near-white
};

// Additional segment colors for categorical distributions
const SEG_COLORS = [
  '#4f46e5', // indigo-600
  '#f59e0b', // amber-500
  '#06b6d4', // cyan-500
  '#ef4444', // red-500
  '#a78bfa', // violet-300
  '#14b8a6', // teal-500
  '#fb7185', // rose-400
  '#22c55e', // green-500 (used sparingly)
];

const MATRIX_GLOBAL_LIMIT = 5;
const POSITIVE_KEYWORDS = ['excellent', 'amazing', 'great', 'good', 'love', 'satisfied', 'happy', 'yes', 'positive', 'like'];
const NEGATIVE_KEYWORDS = ['bad', 'poor', 'terrible', 'hate', 'no', 'negative', 'dislike', 'angry', 'unsatisfied', 'worse'];
const NEUTRAL_KEYWORDS = ['neutral', 'maybe', 'ok', 'average', 'fine', 'unsure'];

function classifyOptionLabel(label) {
  if (!label) return 'Other';
  const lower = label.toLowerCase();
  if (POSITIVE_KEYWORDS.some((kw) => lower.includes(kw))) return 'Positive';
  if (NEGATIVE_KEYWORDS.some((kw) => lower.includes(kw))) return 'Negative';
  if (NEUTRAL_KEYWORDS.some((kw) => lower.includes(kw))) return 'Neutral';
  if (/^(none|n\/a|na|null|nothing|not sure)/i.test(label)) return 'Neutral';
  return 'Other';
}

/** Media query hook for responsive tweaks */
function useIsNarrow(maxW = 560) {
  const [narrow, setNarrow] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= maxW;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setNarrow(window.innerWidth <= maxW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [maxW]);
  return narrow;
}

/** Small KPI chip */
function Kpi({ label, value, sub, fullWidth = false }) {
  return (
    <div className="kpi-chip" style={{
      display: 'inline-flex', flexDirection: 'column', padding: '8px 12px',
      border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12,
      marginRight: fullWidth ? 0 : 8,
      marginBottom: fullWidth ? 8 : 0,
      minWidth: fullWidth ? 'auto' : 96,
      width: fullWidth ? '100%' : 'auto'
    }}>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

/* ---------- WOW add-ons ---------- */

function Sparkline({ data, width = 96 }) {
  const safe = Array.isArray(data) ? data : [];
  return (
    <div style={{ width, height: 28 }}>
      <ResponsiveContainer>
        <LineChart data={safe}>
          <Line type="monotone" dataKey="count" stroke={CHART_COLORS.line} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function SentimentGauge({ pct = 0 }) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  const data = [{ name: 'sent', value: clamped }];
  return (
    <div style={{ width: 84, height: 84, position: 'relative' }}>
      <ResponsiveContainer>
        <RadialBarChart
          data={data}
          cx="50%" cy="50%"
          innerRadius="70%" outerRadius="100%"
          startAngle={180} endAngle={0}
          barSize={8}
        >
          <RadialBar dataKey="value" cornerRadius={8} fill={CHART_COLORS.barAlt} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700
      }}>
        {clamped}%
      </div>
    </div>
  );
}

function Highlights({ series, sentimentAvgPct }) {
  const notes = [];
  if (Array.isArray(series) && series.length > 3) {
    const last = series.at(-1)?.count ?? 0;
    const prev = series.at(-2)?.count ?? 0;
    if (prev > 0 && last >= prev * 1.4 && last >= 5) {
      const delta = Math.round(((last - prev) / Math.max(1, prev)) * 100);
      notes.push(`Responses spiked ${delta}% in the most recent bucket.`);
    }
  }
  if (Number.isFinite(sentimentAvgPct) && sentimentAvgPct < 45) {
    notes.push('Sentiment is trending low — review negative keywords.');
  }
  if (!notes.length) return null;
  return (
    <div className="card" style={{ marginBottom: 8, background: 'var(--muted-bg,#f9fafb)' }}>
      <ul style={{ margin: 8, paddingLeft: 16 }}>
        {notes.map((n, i) => <li key={i} style={{ fontSize: 13 }}>{n}</li>)}
      </ul>
    </div>
  );
}

/** Format AI summary into bullets and emphasize numbers */
function EmphasizeNumbers({ text }) {
  const parts = [];
  const re = /\b(\d{1,3}(?:,\d{3})*|\d+)(%?)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={`${m.index}`}>{m[1]}{m[2]}</strong>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function toBullets(text) {
  if (!text) return [];
  // Prefer explicit line breaks; fallback to simple sentence-ish split.
  const lines = text.split(/\n+/).flatMap((ln) => ln.split(/(?<=[.!?])\s+(?=[A-Z0-9])/));
  return lines.map((s) => s.trim()).filter(Boolean);
}

/*
  AnalyticsPanel — MC Breakdown toggle and correctness:

  ✅ Percent mode divides by the per-question total (sum of option counts),
     not the survey-level response count. This is correct for:
       - skipped questions
       - multi-select (shows % of answers recorded for that question)
  Tooltip shows: "<Option> — <count> (<pct>%)"
*/
function AnalyticsPanel({ surveyId, analytics, loading, error, onRefresh, onExportHtml, exportHtmlRestricted = false }) {
  const [mcShowPct, setMcShowPct] = useState(true);
  const isNarrow = useIsNarrow(560);
  const [copied, setCopied] = useState(false);
  const [kwView, setKwView] = useState('bars'); // 'chips' | 'bars' | 'treemap'
  const [kwLimit, setKwLimit] = useState(10);
  const [mcView, setMcView] = useState('matrix'); // 'detail' | 'grid' | 'matrix'
  const [matrixActiveQuestionId, setMatrixActiveQuestionId] = useState(null);

  const topKeywords = useMemo(() => {
    const arr = Array.isArray(analytics?.top_keywords) ? analytics.top_keywords : [];
    return arr
      .map(k => ({ term: k.term ?? String(k[0]), count: Number(k.count ?? k[1] ?? 0) }))
      .sort((a, b) => b.count - a.count);
  }, [analytics]);

  const series = useMemo(() => {
    const base = (analytics?.resp_count_timeseries || []).map(d => ({
      bucket: d.bucket,
      count: Number(d.count || 0)
    }));
    // Compute trailing moving average (window=7)
    const window = 7;
    let sum = 0;
    return base.map((d, i) => {
      sum += d.count;
      if (i >= window) sum -= base[i - window].count;
      const denom = Math.min(i + 1, window);
      const ma = denom > 0 ? Math.round((sum / denom) * 10) / 10 : 0;
      return { ...d, ma };
    });
  }, [analytics]);

  const trend7 = useMemo(() => {
    const counts = (series || []).map(d => d.count);
    if (!counts.length) return null;
    const last7 = counts.slice(-7).reduce((a, b) => a + b, 0);
    const prev7 = counts.slice(-14, -7).reduce((a, b) => a + b, 0);
    const delta = last7 - prev7;
    const pct = prev7 > 0 ? Math.round((delta / prev7) * 100) : (last7 > 0 ? 100 : 0);
    return { last7, prev7, delta, pct, dir: delta >= 0 };
  }, [series]);

  // Top keywords helpers for views
  const kwAll = useMemo(() => topKeywords || [], [topKeywords]);
  const kwLimitClamped = useMemo(() => {
    const desired = Number.isFinite(kwLimit) ? kwLimit : 10;
    return Math.min(Math.max(desired, 1), 50);
  }, [kwLimit]);
  const kwTop = useMemo(() => kwAll.slice(0, kwLimitClamped), [kwAll, kwLimitClamped]);
  const kwMax = useMemo(() => kwTop.reduce((m, k) => Math.max(m, Number(k.count || 0)), 0), [kwTop]);
  const kwTotalShown = useMemo(() => kwTop.reduce((s, k) => s + Number(k.count || 0), 0), [kwTop]);
  const kwTotalAll = useMemo(() => kwAll.reduce((s, k) => s + Number(k.count || 0), 0), [kwAll]);
  const kwCoveragePct = useMemo(() => kwTotalAll ? Math.round((kwTotalShown / kwTotalAll) * 100) : 0, [kwTotalShown, kwTotalAll]);

  /**
   * mc_breakdown = { [questionId]: { [optionLabel]: count } }
   * Example: { "11": {"Somewhat Likely": 1}, "13": {"Satisfied": 1} }
   * Meaning: for each MC question, this maps each option to how many times it was chosen.
   * We sum counts per question to get a per-question total for % calculations.
   */
  const mcCharts = useMemo(() => {
    const out = [];
    const mc = analytics?.mc_breakdown || {};
    const qmap = analytics?.questions_map || {}; // <-- use backend-provided map when available
    for (const qid of Object.keys(mc)) {
      const optMap = mc[qid] || {};
      const rows = Object.keys(optMap)
        .map(opt => ({ option: opt, count: Number(optMap[opt] || 0) }))
        .sort((a, b) => b.count - a.count);
      const questionTotal = rows.reduce((a, b) => a + b.count, 0);
      const qtext = qmap[qid] || qid; // fallback to qid if text missing
      out.push({ qid, qtext, rows, questionTotal });
    }
    return out;
  }, [analytics]);

  const sentimentAvgPct = useMemo(() => {
    // Treat null/undefined as "no data"
    const raw = analytics?.sentiment_avg;
    if (raw === null || raw === undefined) return null;
    const v = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(v)) return null;
    const clamped = Math.max(-1, Math.min(1, v));
    return Math.round(((clamped + 1) / 2) * 100);
  }, [analytics]);

  // Helpers for MC views
  const mcTopOptionsGlobal = useMemo(() => {
    const totals = new Map();
    for (const q of mcCharts) {
      for (const r of q.rows) {
        totals.set(r.option, (totals.get(r.option) || 0) + Number(r.count || 0));
      }
    }
    const all = Array.from(totals.entries()).map(([opt, sum]) => ({ option: String(opt), sum }));
    all.sort((a, b) => b.sum - a.sum);
    return all;
  }, [mcCharts]);

  const matrixK = isNarrow ? 4 : 5;
  const matrixGlobalTop = useMemo(() => mcTopOptionsGlobal.slice(0, MATRIX_GLOBAL_LIMIT).map(x => x.option), [mcTopOptionsGlobal]);

  const matrixQuestions = useMemo(() => {
    const baseTop = matrixGlobalTop.slice(0, matrixK);
    return mcCharts.map(({ qid, qtext, rows, questionTotal }) => {
      const total = Number(questionTotal || 0);
      const options = rows.map((r) => ({ option: r.option, count: Number(r.count || 0) }));
      const countsMap = new Map(options.map((r) => [r.option, r.count]));
      const smallQuestion = options.length <= matrixK;

      const columns = [];
      const used = new Set();

      const addColumn = (label, type, count) => {
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        columns.push({ key: `${type}:${label}`, label, type, count, pct });
      };

      const baseColumns = smallQuestion
        ? options.map((r) => r.option)
        : baseTop;

      baseColumns.forEach((label) => {
        if (!label) return;
        const count = countsMap.get(label) || 0;
        used.add(label);
        addColumn(label, 'option', count);
      });

      const bucketTotals = new Map();
      if (!smallQuestion) {
        options.forEach(({ option, count }) => {
          if (used.has(option)) return;
          const bucket = classifyOptionLabel(option);
          bucketTotals.set(bucket, (bucketTotals.get(bucket) || 0) + count);
        });
        ['Positive', 'Neutral', 'Negative', 'Other'].forEach((bucket) => {
          const count = bucketTotals.get(bucket) || 0;
          if (count > 0) addColumn(bucket, 'bucket', count);
        });
      }

      return {
        qid,
        qtext,
        total,
        columns,
        smallQuestion,
      };
    });
  }, [mcCharts, matrixGlobalTop, matrixK]);

  useEffect(() => {
    if (!matrixActiveQuestionId && matrixQuestions.length) {
      setMatrixActiveQuestionId(matrixQuestions[0].qid);
    }
  }, [matrixQuestions, matrixActiveQuestionId]);

  const activeMatrixQuestion = useMemo(() => {
    if (!matrixQuestions.length) return null;
    const found = matrixQuestions.find((q) => q.qid === matrixActiveQuestionId);
    return found || matrixQuestions[0];
  }, [matrixQuestions, matrixActiveQuestionId]);

  function buildSegments(rows, total, topN = 3) {
    const segs = [];
    const sorted = [...rows].sort((a, b) => b.count - a.count);
    let used = 0;
    sorted.slice(0, topN).forEach((r, i) => {
      const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
      used += r.count;
      segs.push({ label: r.option, pct, count: r.count, color: SEG_COLORS[i % SEG_COLORS.length] });
    });
    const other = total - used;
    if (other > 0) {
      const pct = total > 0 ? Math.max(0, 100 - segs.reduce((s, x) => s + x.pct, 0)) : 0;
      segs.push({ label: 'Other', pct, count: other, color: '#9ca3af' });
    }
    return segs;
  }

  function MiniStackBar({ segments }) {
    const height = 16;
    return (
      <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 8, overflow: 'hidden', width: '100%', height }}>
        <div style={{ display: 'flex', width: '100%', height: '100%' }}>
          {segments.map((s, idx) => (
            <div key={idx} title={`${s.label} — ${s.pct}% (${s.count})`} style={{ width: `${Math.max(0, s.pct)}%`, background: s.color, height: '100%', position: 'relative' }}>
              {s.pct >= 15 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                  {s.pct}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const totalResponses = Number(analytics?.resp_count || 0);

  return (
    <div className="card" style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700 }}>Insights</div>
        <button className="btn btn-small" onClick={onRefresh} disabled={loading} title="Refresh analytics">
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          className="btn btn-small"
          onClick={async () => {
            if (!onExportHtml) return;
            try {
              await onExportHtml();
            } catch (err) {
              console.error('export html failed:', err?.message || err);
            }
          }}
          title="Export responses + insights (HTML)"
          disabled={!onExportHtml}
        >
          Export HTML
        </button>
        {exportHtmlRestricted && (
          <span className="muted" style={{ fontSize: 12 }}>
            Export requires a code.
          </span>
        )}
        {mcCharts.length > 0 && mcView === 'detail' && (
          <button
            className="btn btn-small"
            onClick={() => setMcShowPct(v => !v)}
            title={mcShowPct ? 'Show raw counts' : 'Show % of answers for each question'}
          >
            {mcShowPct ? 'Show counts' : 'Show %'}
          </button>
        )}
      </div>

      {error && <div className="error" style={{ marginBottom: 8 }}>Analytics error: {String(error)}</div>}
      {loading && !analytics && <div>Loading analytics…</div>}

      {analytics && (
        <>
          <Highlights series={series} sentimentAvgPct={sentimentAvgPct ?? null} />

          <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8, alignItems: isNarrow ? 'stretch' : 'center', flexDirection: isNarrow ? 'column' : 'row' }}>
            <Kpi
              label="Responses"
              value={totalResponses}
              sub={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkline data={(series || []).slice(-7)} width={isNarrow ? 72 : 96} />
                  {trend7 && (
                    <span style={{ color: trend7.dir ? '#059669' : '#dc2626' }}>
                      {trend7.dir ? '▲' : '▼'} {Math.abs(trend7.pct)}% vs prior 7
                    </span>
                  )}
                </div>
              }
              fullWidth={isNarrow}
            />
            <Kpi label="Last Updated" value={analytics.updated_at ? new Date(analytics.updated_at).toLocaleString() : '—'} fullWidth={isNarrow} />
          </div>

          {/* AI Summary moved above charts with bullets + copy */}
          <div style={{ marginTop: 10, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 15, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>AI Summary</div>
              <button
                className="btn btn-small"
                onClick={async () => {
                  try {
                    await (navigator?.clipboard?.writeText ? navigator.clipboard.writeText(String(analytics.llm_summary || '')) : Promise.reject());
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  } catch (_) {
                    // no clipboard API; ignore
                  }
                }}
                disabled={!analytics.llm_summary}
                title={analytics.llm_summary ? 'Copy summary' : 'No summary to copy'}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div style={{
              background: 'var(--muted-bg,#f8fafc)',
              border: '1px solid var(--border-color,#e5e7eb)',
              borderRadius: 12, padding: 12, lineHeight: 1.4,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word'
            }}>
              {(() => {
                const txt = analytics.llm_summary || '';
                const bullets = toBullets(txt);
                if (!bullets.length) return 'No summary yet.';
                return (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {bullets.map((b, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <EmphasizeNumbers text={b} />
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Responses Over Time</div>
            <div style={{ height: isNarrow ? 180 : 220 }}>
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 10, right: 14, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="4 4" />
                  <XAxis dataKey="bucket" tick={{ fontSize: isNarrow ? 10 : 12, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
                  <YAxis allowDecimals={false} tick={{ fontSize: isNarrow ? 10 : 12, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
                  <Tooltip
                    cursor={{ stroke: CHART_COLORS.grid }}
                    contentStyle={{ background: CHART_COLORS.tooltipBg, border: '1px solid #374151', borderRadius: 8, color: CHART_COLORS.tooltipFg }}
                  />
                  <Line type="monotone" dataKey="count" name="This survey" stroke={CHART_COLORS.line} strokeWidth={2} dot={{ r: 2, fill: CHART_COLORS.lineDot }} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="ma" name="7-pt MA" stroke={CHART_COLORS.line2} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {(mcCharts.length > 0) && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
              <div style={{ fontSize: 15, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Multiple Choice</div>
                <button className="btn btn-small" onClick={() => setMcView('matrix')} disabled={mcView==='matrix'}>Matrix</button>
                <button className="btn btn-small" onClick={() => setMcView('detail')} disabled={mcView==='detail'}>Detail</button>
                <button className="btn btn-small" onClick={() => setMcView('grid')} disabled={mcView==='grid'}>Grid</button>
                {mcView==='detail' && (
                  <button
                    className="btn btn-small"
                    onClick={() => setMcShowPct(v => !v)}
                    title={mcShowPct ? 'Show raw counts' : 'Show % of answers for each question'}
                  >
                    {mcShowPct ? 'Show counts' : 'Show %'}
                  </button>
                )}
              </div>

              {mcView === 'grid' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
                  {mcCharts.map(({ qid, qtext, rows, questionTotal }) => {
                    const segs = buildSegments(rows, questionTotal, 3);
                    return (
                      <div key={qid} className="card" style={{ padding: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }} title={String(qtext)}>
                          {String(qtext || '').length > 90 ? `${String(qtext).slice(0, 87)}…` : String(qtext || '')}
                        </div>
                        <MiniStackBar segments={segs} />
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          {segs.slice(0, 3).map((s, i) => (
                            <span key={i} className="badge" title={`${s.label} — ${s.pct}% (${s.count})`} style={{ background: s.color, color: '#fff' }}>
                              {s.label} {s.pct}%
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {mcView === 'matrix' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                  {matrixQuestions.map((q) => {
                    const isActive = activeMatrixQuestion?.qid === q.qid;
                    return (
                      <div
                        key={q.qid}
                        style={{
                          border: isActive ? '2px solid #6366f1' : '1px solid #e5e7eb',
                          borderRadius: 12,
                          padding: 12,
                          background: '#fff',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          boxShadow: isActive ? '0 4px 16px rgba(99,102,241,0.12)' : 'none'
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setMatrixActiveQuestionId(q.qid)}
                          style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            width: '100%',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            marginBottom: 10
                          }}
                        >
                          <span className="badge" style={{ background: isActive ? '#6366f1' : '#e5e7eb', color: isActive ? '#fff' : '#111827' }}>
                            {q.smallQuestion ? 'Full view' : 'Top + buckets'}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                            {String(q.qtext || '').length > 90 ? `${String(q.qtext).slice(0, 87)}…` : String(q.qtext || '')}
                          </span>
                          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#475569' }}>
                            Total responses: {q.total}
                          </span>
                        </button>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(96px, 1fr))`, gap: 8 }}>
                          {q.columns.map((col) => {
                            const alpha = col.pct > 0 ? Math.max(0.12, Math.min(0.95, col.pct / 95)) : 0;
                            const bg = col.type === 'bucket'
                              ? `rgba(15, 118, 110, ${alpha})`
                              : `rgba(79, 70, 229, ${alpha})`;
                            const textColor = col.pct >= 55 ? '#fff' : '#111827';
                            return (
                              <div
                                key={col.key}
                                title={`${col.label} — ${col.pct}% (${col.count})`}
                                style={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 10,
                                  padding: '10px 12px',
                                  background: col.pct > 0 ? bg : '#f8fafc',
                                  color: textColor,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 4,
                                  minHeight: 70
                                }}
                              >
                                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>{col.label}</div>
                                <div style={{ fontSize: 20, fontWeight: 700 }}>{col.pct}%</div>
                                <div style={{ fontSize: 11, opacity: 0.8 }}>{col.count} responses</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {mcView === 'detail' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 }}>
                  {mcCharts.map(({ qid, qtext, rows, questionTotal }) => {
                    // Build data for chart
                    const data = rows.map(r => {
                      const pct = questionTotal > 0 ? Math.round((r.count / questionTotal) * 100) : 0;
                      return mcShowPct
                        ? { ...r, value: pct, _pct: pct }     // percent mode
                        : { ...r, value: r.count, _pct: pct }; // count mode, but keep _pct for tooltip
                    });
                    const yMax = mcShowPct ? 100 : undefined;
                    const top = rows[0];
                    const topPct = top ? (questionTotal > 0 ? Math.round((top.count / questionTotal) * 100) : 0) : null;

                    return (
                  <div key={qid}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: isNarrow ? 'flex-start' : 'center', gap: 8, flexWrap: 'wrap', flexDirection: isNarrow ? 'column' : 'row' }}>
                      <span className="badge" title="Multiple choice question">Multiple choice</span>
                      <span title={qtext} aria-label={qtext} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {String(qtext || '').length > 80 ? `${String(qtext).slice(0, 77)}…` : String(qtext || '')}
                      </span>
                      <span style={{ fontWeight: 400, opacity: 0.7 }}>
                        {mcShowPct ? '(%)' : '(count)'}
                      </span>
                      {top && (
                        <span className="badge" title={`Top option: ${top.option} (${topPct}%)`}>
                          Top: {String(top.option)} ({topPct}%)
                        </span>
                      )}
                      <span style={{ marginLeft: 'auto', fontWeight: 400, opacity: 0.7 }}>
                        Total responses recorded: {questionTotal}
                      </span>
                    </div>
                    <div style={{ height: isNarrow ? 280 : 320, overflow: 'hidden' }}>
                      <ResponsiveContainer>
                        <BarChart
                          key={`${qid}-${mcShowPct ? 'pct' : 'count'}`}
                          data={data}
                          margin={{ top: 10, right: 14, bottom: 28, left: 0 }}
                        >
                          <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="4 4" />
                          <XAxis dataKey="option" tick={{ fontSize: 12, fill: CHART_COLORS.axis }} tickMargin={8} stroke={CHART_COLORS.grid} />
                          <YAxis
                            allowDecimals={false}
                            domain={yMax ? [0, yMax] : ['auto', 'auto']}
                            tick={{ fontSize: 12, fill: CHART_COLORS.axis }}
                            tickFormatter={(v) => mcShowPct ? `${v}%` : `${v}`}
                            stroke={CHART_COLORS.grid}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                            contentStyle={{ background: CHART_COLORS.tooltipBg, border: '1px solid #374151', borderRadius: 8, color: CHART_COLORS.tooltipFg }}
                            formatter={(value, name, props) => {
                              const raw = props?.payload?.count ?? 0;
                              const pct = props?.payload?._pct ?? 0;
                              return mcShowPct
                                ? [`${value}%`, `${raw} votes`]
                                : [raw, `${pct}%`];
                            }}
                            labelFormatter={(label) => `Option: ${label}`}
                          />
                          <Bar dataKey="value" fill={CHART_COLORS.barAlt} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Top Keywords</div>
              {!!kwAll.length && (
                <>
                  <button className="btn btn-small" onClick={() => setKwView('bars')} disabled={kwView==='bars'}>Bars</button>
                  <button className="btn btn-small" onClick={() => setKwView('treemap')} disabled={kwView==='treemap'}>Treemap</button>
                  <button className="btn btn-small" onClick={() => setKwView('chips')} disabled={kwView==='chips'}>Chips</button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    Show
                    <select
                      value={kwLimitClamped}
                      onChange={(e) => setKwLimit(Number(e.target.value))}
                      style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 6px', fontSize: 12 }}
                    >
                      {[10, 20, 30, 40, 50].map((limit) => (
                        <option key={limit} value={limit}>{limit}</option>
                      ))}
                    </select>
                    keywords
                  </label>
                  <span className="badge" title={`Top ${kwTop.length} cover ${kwCoveragePct}% of all mentions`}>
                    Top {kwTop.length} cover {kwCoveragePct}%
                  </span>
                </>
              )}
            </div>
            {!kwAll.length ? (
              <div style={{ opacity: 0.7 }}>No keywords yet.</div>
            ) : kwView === 'bars' ? (
              <div style={{ height: Math.max(180, kwTop.length * 28 + 40) }}>
                <ResponsiveContainer>
                  <BarChart data={kwTop} layout="vertical" margin={{ top: 10, right: 14, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="4 4" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} domain={[0, kwMax || 'auto']} />
                    <YAxis type="category" dataKey="term" width={isNarrow ? 80 : 120} tick={{ fontSize: 12, fill: CHART_COLORS.axis }} stroke={CHART_COLORS.grid} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ background: CHART_COLORS.tooltipBg, border: '1px solid #374151', borderRadius: 8, color: CHART_COLORS.tooltipFg }} formatter={(v, n) => [v, 'mentions']} />
                    <Bar dataKey={(d)=>Number(d.count||0)} name="Mentions" fill={CHART_COLORS.bar} radius={[4, 4, 4, 4]}>
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : kwView === 'treemap' ? (
              <div style={{ height: isNarrow ? 220 : 260 }}>
                <ResponsiveContainer>
                  <Treemap data={kwTop.map(k => ({ name: String(k.term), size: Number(k.count||0) }))} dataKey="size" stroke="#fff" fill={CHART_COLORS.barAlt} />
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {kwTop.map((k, idx) => (
                  <span
                    key={idx}
                    className="badge"
                    title={`${String(k.term)} — ${Number(k.count||0)} mentions`}
                    style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    onClick={() => {
                      try { navigator?.clipboard?.writeText && navigator.clipboard.writeText(String(k.term)); } catch (_) {}
                    }}
                  >
                    {k.term} • {k.count}
                  </span>
                ))}
              </div>
            )}
          </div>

          
        </>
      )}
    </div>
  );
}

export default AnalyticsPanel;
